import React, { useState } from "react";
import { Box, Button, Card, CardContent, TextField, Typography, FormControlLabel, Checkbox, InputAdornment, IconButton } from "@mui/material";
import { Link } from "react-router-dom"; // Import Link for routing
import Signinlogo from "../../assets/signinlogo.png";
import { useNavigate } from "react-router-dom";
import Colors from "../libs/Colors.js";
import axios from 'axios'; // Import axios for API requests
import { hosturl } from "../libs/Constant.js";
import Visibility from '@mui/icons-material/Visibility'; // Import Visibility Icon
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // Import VisibilityOff Icon

const SignupPage = () => {
  const navigate = useNavigate();

  // State to store form values
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    country: "",
    password: "",
    confirmPassword: "",
  });

  // State to manage form submission status and errors
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");  // Added state for success message
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State for toggling password visibility
  const [isChecked, setIsChecked] = useState(false); // State to track checkbox

  const handlesignin = () => {
    navigate("/"); // Navigate to the login page
  };

  // Handle form field changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked); // Update the checkbox state
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!formData.name || !formData.phoneNumber || !formData.email || !formData.country || !formData.password) {
      setError("All fields are required.");
      return;
    }
  
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordPattern.test(formData.password)) {
      setError("Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.");
      return;
    }
  
    try {
      setLoading(true);
      const response = await axios.post(`${hosturl}/api/ebooster/auth/signup`, {
        email: formData.email,
        name: formData.name,
        password: formData.password,
        country: formData.country,
        phoneNumber: formData.phoneNumber,
      });
  
      console.log("Signup response:", response); // Debugging the response
  
      if (response.status === 200) {  // Adjusted to check for 200
        setError("");
        setSuccessMessage("Account created successfully! Please verify your OTP.");
        console.log("Navigating to /verify-otp with email:", formData.email); // Debugging navigation
        navigate("/verify-otp", {
          state: { email: formData.email },
        });
      } else {
        setError("Something went wrong, please try again.");
        setSuccessMessage("");
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.response && error.response.data && error.response.data.message) {
        if (error.response.data.message === "User with same email already exists") {
          setError("The email address is already registered. Please use a different email.");
        } else {
          setError("An error occurred. Please try again.");
        }
        setSuccessMessage("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        background: "linear-gradient(to bottom, #44A08D, #E4DDEF)",
        height: '100%',
        overflowY: "auto",
      }}
    >
      {/* Left Side - Image & Gradient */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: "20px", md: "0" },
        }}
      >
        <img
          src={Signinlogo}
          alt="Illustration"
          style={{ width: "80%", height: "auto" }}
        />
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: "20px", md: "40px" },
          backgroundColor: 'white',
          borderStartStartRadius: '120px',
          borderBottomLeftRadius: '120px',
        }}
      >
        <Card
          sx={{
            width: { xs: "80%", sm: "400px" },
            boxShadow: "none",
            padding: "20px",
            marginTop: { xs: "20px", md: "0" },
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ marginBottom: "10px" }}>
              Create an Account
            </Typography>

            {/* Form Fields */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                margin="dense"
                variant="standard"
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={{ marginBottom: "8px" }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                margin="dense"
                variant="standard"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                sx={{ marginBottom: "8px" }}
              />
              <TextField
                fullWidth
                label="Email"
                margin="dense"
                variant="standard"
                name="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ marginBottom: "8px" }}
              />
              <TextField
                fullWidth
                label="Country"
                margin="dense"
                variant="standard"
                name="country"
                value={formData.country}
                onChange={handleChange}
                sx={{ marginBottom: "8px" }}
              />
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"} 
                label="Password"
                margin="dense"
                variant="standard"
                name="password"
                value={formData.password}
                onChange={handleChange}
                sx={{ marginBottom: "8px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
        
              {/* Terms and Privacy Checkbox */}
              <FormControlLabel
                control={<Checkbox checked={isChecked} onChange={handleCheckboxChange} />}
                label={
                  <Typography variant="body2" sx={{ fontSize: "11px" }}>
                    By creating an account, you agree with our{" "}
                    <a href="https://ebooster.online/terms-and-conditions" target="_blank" style={{ color: "blue", textDecoration: "underline" }}>
                      Terms of service
                    </a>{" "}
                    and{" "}
                    <a href="https://ebooster.online/privacy-policy" target="_blank"style={{color:"blue", textDecoration: "underline" }}>
                      Privacy Policy
                    </a>, 
                    and our default Notification Settings.
                  </Typography>
                }
                sx={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              />

              {/* Error Message */}
              {error && (
                <Typography variant="body2" color="error" align="center" sx={{ fontWeight: "bold", fontFamily:'poppins' }}>
                  {error}
                </Typography>
              )}

              {/* Success Message */}
              {successMessage && (
                <Box
                  sx={{
                    backgroundColor: "green",
                    color: "white",
                    padding: "10px",
                    borderRadius: "4px",
                    marginTop: "10px",
                  }}
                >
                  <Typography variant="body2" align="center" style={{fontFamily:'poppins'}}>
                    {successMessage}
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  marginTop: "20px",
                  background: Colors.buttonColor,
                  borderRadius: "8px",
                  textTransform: "none",
                }}
                disabled={loading || !isChecked} // Disable button while loading or if checkbox is not checked
              >
                {loading ? "Registering..." : "Register"}
              </Button>
            </form>

            {/* Link to login page */}
            <Typography variant="body2" align="center" sx={{ marginTop: "10px" }}>
              Already have an account?{" "}
              <Button
                onClick={handlesignin}
                style={{
                  color: "blue",
                  background: "none",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Login
              </Button>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SignupPage;
